import { createClient } from '@supabase/supabase-js';
import { isDemoMode } from './demo/store';
import { getDemoSupabaseSession } from './demo/session';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('[Supabase] VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY not set, auth will not work.');
}

const realClient = createClient(supabaseUrl, supabaseAnonKey);

// Wrap auth so demo mode returns a synthetic session for every getSession /
// getUser call without ever touching Supabase. Sign-in/sign-up methods are
// also short-circuited so the demo can't accidentally hit the real backend.
const demoAuthHandler: ProxyHandler<typeof realClient.auth> = {
  get(target, prop, receiver) {
    if (!isDemoMode()) {
      return Reflect.get(target, prop, receiver);
    }
    switch (prop) {
      case 'getSession':
        return async () => ({ data: { session: getDemoSupabaseSession() }, error: null });
      case 'getUser':
        return async () => ({ data: { user: getDemoSupabaseSession().user }, error: null });
      case 'refreshSession':
        return async () => ({ data: { session: getDemoSupabaseSession(), user: getDemoSupabaseSession().user }, error: null });
      case 'signOut':
        return async () => ({ error: null });
      case 'onAuthStateChange':
        // Fire SIGNED_IN once on subscribe so any code waiting for the event
        // proceeds, then no-op.
        return (cb: (event: string, session: ReturnType<typeof getDemoSupabaseSession>) => void) => {
          queueMicrotask(() => cb('SIGNED_IN', getDemoSupabaseSession()));
          return { data: { subscription: { unsubscribe: () => {} } } };
        };
      case 'signInWithPassword':
      case 'signUp':
      case 'signInWithOtp':
      case 'verifyOtp':
        return async () => ({ data: { session: getDemoSupabaseSession(), user: getDemoSupabaseSession().user }, error: null });
      default: {
        const v = Reflect.get(target, prop, receiver);
        return typeof v === 'function' ? v.bind(target) : v;
      }
    }
  },
};

const wrappedAuth = new Proxy(realClient.auth, demoAuthHandler);

// Spread the real client but swap auth for the wrapped one. Other namespaces
// (e.g. .from()) are unused in demo because the api.ts intercept catches every
// data call before it gets here.
export const supabase = new Proxy(realClient, {
  get(target, prop, receiver) {
    if (prop === 'auth') return wrappedAuth;
    return Reflect.get(target, prop, receiver);
  },
}) as typeof realClient;
